<template>
  <ion-page>
    <ion-content>
      <div class="banner-container">
        <BannerComponent
          :src="isMobile ? mobileBannerSrc : desktopBannerSrc"
          title="MESSAGES"
          description="Messages your team has sent about this request."
        />
      </div>
      <ion-grid :fixed="true" class="ion-padding">
        <ion-row class="ion-align-items-center ion-padding-bottom">
          <ion-col>
            <h2>Messages</h2>
          </ion-col>
          <ion-col size="auto">
            <ion-button fill="clear" @click="goBack">Back to Requests</ion-button>
          </ion-col>
        </ion-row>

        <MessageList
          :messages="messages"
          :is-loading="isLoading"
          :has-loaded="hasLoaded"
          :new-message-ids="newMessageIds"
          empty-text="No messages for this request yet."
          @upload-photo="prepareModal"
        />
      </ion-grid>
      <FooterComponent />
    </ion-content>
    <AdditionalPhotoUploadModalComponent
      v-if="selectedClaimId"
      ref="photoModal"
      :claimId="selectedClaimId"
    />
  </ion-page>
</template>

<script setup>
import {
  IonPage,
  IonContent,
  IonGrid,
  IonRow,
  IonCol,
  IonButton,
  onIonViewWillEnter,
  onIonViewWillLeave,
} from '@ionic/vue';
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useCustomerStore } from '../store/customer';
import { useBrandingStore } from '../store/branding';
import { useUnreadMessages } from '@/composables/useUnreadMessages';
import BannerComponent from '@/components/BannerComponent.vue';
import FooterComponent from '@/components/FooterComponent.vue';
import MessageList from '@/components/MessageList.vue';
import AdditionalPhotoUploadModalComponent from '@/components/AdditionalPhotoUploadModalComponent.vue';

const route = useRoute();
const router = useRouter();
const customerStore = useCustomerStore();
const brandingStore = useBrandingStore();
const { isLoading, hasLoaded, newMessageIds, load, stopTracking } = useUnreadMessages();

const selectedClaimId = ref(null);
const photoModal = ref(null);

const desktopBannerSrc = ref(brandingStore.brand.desktop_banner_6);
const mobileBannerSrc = ref(brandingStore.brand.mobile_banner_6);
const screenWidth = ref(window.innerWidth);
const isMobile = computed(() => screenWidth.value < 768);
const handleResize = () => {
  screenWidth.value = window.innerWidth;
};

const claimId = computed(() => route.params.claimId);
// Claims already carry .product and .claim_no from associateClaimsAndPolicies - same lookup
// MessagesPage.vue uses for its per-message title. Embedded onto each message so MessageList's
// card rendering doesn't need to know whether it's showing one claim's messages or many.
const claim = computed(() => customerStore.claims.find((c) => c.id === claimId.value));
const messages = computed(() =>
  (customerStore.messagesByClaimId[claimId.value] || []).map((message) => ({
    ...message,
    claim: claim.value,
  }))
);

function goBack() {
  router.push({ name: 'requests' });
}

function prepareModal(id) {
  selectedClaimId.value = Number(id);
  // Use a timeout to ensure the DOM has updated before opening the modal - matches
  // RequestsPage.vue's own use of this component.
  setTimeout(() => {
    if (photoModal.value && photoModal.value.modal) {
      photoModal.value.modal.$el.present();
    }
  }, 0);
}

onMounted(() => {
  window.addEventListener('resize', handleResize);
});

onUnmounted(() => {
  window.removeEventListener('resize', handleResize);
});

// Captures the requested claim id once up front rather than re-reading the live claimId
// computed after the await - ion-router-outlet reuses this same component instance across a
// claimId param change, so a slow-resolving fetch for the claim navigated away from could
// otherwise apply its result under a claimId that's since moved on to a different claim.
async function fetchCurrentClaimMessages() {
  const requestedClaimId = claimId.value;
  const [, messagesLoaded] = await Promise.all([
    customerStore.fetchClaims(),
    customerStore.fetchMessagesForClaim(requestedClaimId),
  ]);
  return messagesLoaded;
}

// ion-router-outlet keeps this page's component instance alive across navigations, so a plain
// onMounted would only ever fire once per app session - see useUnreadMessages.js for why
// onIonViewWillEnter/onIonViewWillLeave and the re-entrancy guard are used instead.
onIonViewWillEnter(() => load(fetchCurrentClaimMessages, () => messages.value));
onIonViewWillLeave(() => stopTracking());
</script>

<style lang="scss" scoped>
// Clears the fixed nav header in NavigationMenu.vue - same rule every other page defines.
.ion-page {
  margin-top: 125px;
}

@media only screen and (max-width: 768px) {
  .ion-page {
    margin-top: 42px;
  }
}

.banner-container {
  position: relative;
}
</style>
